import { createSelector } from 'reselect';
import { SelectedCustomerSelector } from './customer.selectors';
import { ConvergentBillerAccountDetailsSelector, ConvergentBillerDepositsSelector, ConvergentBillerInvoicesSelector } from './customer.convergent.biller.selectors';
import { AdditionalPropValueType, MetadataConstants, MetadataHelpers, MetadataSelectors, RamdaHelper } from 'invision-core';
import { createTableViewModel, createSelectOptionsForCodes, createSelectOptionsForInvoices, createSelectOptionsForCaseActivities, createDepositsTableViewModel } from '../../components/customer/cases/list/list.selectors.helper';
import { createPreferredContactFromContactPreferences } from '../../components/customer/cases/detail/detail.selectors.helper';
import { CASE_CATEGORIES, CASE_STATUS } from './../../components/customer/cases/list/case.constants';
import { GENERAL_STATUSES } from '../../components/shared/constants/general.status.constants';
import { OrdersSelector } from './order.history.selectors';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import map from 'ramda/src/map';
import pathOr from 'ramda/src/pathOr';
import propEq from 'ramda/src/propEq';
import sortWith from 'ramda/src/sortWith';
import ascend from 'ramda/src/ascend';
import prop from 'ramda/src/prop';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  if (state.customercare.recoverableUiState) {
    return state.customercare.recoverableUiState.cases;
  }
};
export var CustomerCasesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.data.cases;
});
export var CustomerCasePreferredContactSelector = createSelector([SelectedCustomerSelector, MetadataSelectors.codes.MetadataCodeTypeIntegerSelector(MetadataConstants.codes.ContactMethod)], function (selectedCustomer, contactTypes) {
  return createPreferredContactFromContactPreferences(selectedCustomer, contactTypes);
});
export var IsFetchingCasesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.isFetchingCases;
});
export var IsFetchingCaseSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.isFetchingCase;
});
export var IsFetchingCaseActivitiesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.isFetchingCaseActivityTypes;
});
export var IsCreatingCaseSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.isCreatingCase;
});
export var IsUpdatingCaseSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.isUpdatingCase;
});
export var CurrentCaseSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.data.case;
});
export var CurrentCasePortInDetailsSelector = createImmutableSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.data.portInDetails;
});
export var SelectedCaseCategoryIdSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.selectedCaseCategoryId;
});
export var IsCurrentCaseStatusClosedSelector = createSelector([CurrentCaseSelector], function (currentCase) {
  return currentCase && currentCase.Status === CASE_STATUS.CLOSED;
});
export var IsCurrentCaseEditableSelector = createSelector([CurrentCaseSelector], function (currentCase) {
  return currentCase && currentCase.Status !== CASE_STATUS.CLOSED && currentCase.Status !== CASE_STATUS.CANCELED;
});
export var CaseActivityTableDataSelector = createSelector([CurrentCaseSelector], function (currentCase) {
  return currentCase ? currentCase.CaseActivities : [];
});
export var InvoicesMapSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.data.invoices;
});
export var CaseActivityTypesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.data.caseActivityTypes;
});
export var FormattedCaseActivityTypesSelector = createSelector([CaseActivityTypesSelector], function (caseActivityTypes) {
  return caseActivityTypes ? createSelectOptionsForCaseActivities(caseActivityTypes) : [];
});
export var CustomerInvoicesSelector = createSelector([ConvergentBillerInvoicesSelector, InvoicesMapSelector], function () {
  var convergentBillerInvoices = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var caseInvoices = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return convergentBillerInvoices && convergentBillerInvoices.length ? convergentBillerInvoices.map(function (invoice) {
    var subscriberInvoice = caseInvoices[invoice.InvoiceId];
    return subscriberInvoice;
  }).filter(function (invoice) {
    return !!invoice;
  }) : [];
});
export var CaseTypesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.data.caseTypes || [];
});
export var ActiveCaseTypesSelector = createSelector([CaseTypesSelector], function (caseTypes) {
  return filter(function (caseType) {
    return caseType.Status === GENERAL_STATUSES.ACTIVE;
  }, caseTypes);
});
export var DisputeInvoiceCaseTypesSelector = createImmutableSelector([CaseTypesSelector], function (caseTypes) {
  return filter(function (caseType) {
    return caseType.CaseTemplateId === CASE_CATEGORIES.INVOICE_DISPUTE || caseType.IsGlobal;
  }, caseTypes);
});
export var CustomerDespositsSelector = createSelector([ConvergentBillerDepositsSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.DepositType), ConvergentBillerAccountDetailsSelector, OrdersSelector], function () {
  var convergentBillerDeposits = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var subscriberMapDeposits = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var currentAccountSummary = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var orderHistory = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return convergentBillerDeposits && subscriberMapDeposits && orderHistory && convergentBillerDeposits.length && subscriberMapDeposits.length && orderHistory.length ? createDepositsTableViewModel(convergentBillerDeposits, subscriberMapDeposits, currentAccountSummary, orderHistory) : [];
});
export var FormattedInvoicesSelector = createSelector([CustomerInvoicesSelector], function (invoices) {
  return invoices ? createSelectOptionsForInvoices(invoices) : [];
});
export var IsFetchingInvoicesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.isFetchingInvoices;
});
export var CasesErrorSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.casesError;
});
export var RecordCountSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.data.recordCount;
});
export var IncludeStatusFilterSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  if (uiState) {
    return uiState.filterData.includeStatusFilter;
  }
});
export var SelectedPageSizePreference = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.pageSizePreference;
});
export var CurrentPageNumberSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.pageNumber || 0;
});
export var PageCountSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.cases.data.pageCount;
});
export var CaseFormIsInvalidSelector = createSelector([CasesErrorSelector], function (casesError) {
  return casesError ? true : false;
});
var EMPTY_CASES_TABLE = {};
export var CasesTableDataSelector = createSelector([IsFetchingCasesSelector, CustomerCasesSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.CaseStatus), CaseTypesSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.CasePriority), MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.CaseSeverity)], function (isFetchingCases, cases, statusCodes, categoryCodes, priorityCodes, severityCodes) {
  return !isFetchingCases ? createTableViewModel(cases, statusCodes, categoryCodes, priorityCodes, severityCodes) : EMPTY_CASES_TABLE;
});
export var FormattedCasePriorityCodeSelector = createSelector([MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.CasePriority)], function (categoryCodes) {
  return categoryCodes ? createSelectOptionsForCodes(categoryCodes) : null;
});
export var FormattedCaseSeverityCodeSelector = createSelector([MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.CaseSeverity)], function (codes) {
  return codes ? createSelectOptionsForCodes(codes) : null;
});
export var FormattedCaseStatusCodeSelector = createSelector([MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.CaseStatus)], function (codes) {
  return codes ? createSelectOptionsForCodes(codes) : null;
});
export var FormattedCaseCategoryCodeSelector = createSelector([ActiveCaseTypesSelector], function (caseTypes) {
  return map(function (caseType) {
    return {
      text: caseType.DisplayName,
      value: caseType.Id
    };
  }, sortWith([ascend(prop('DisplayName'))], caseTypes));
});
export var FormattedDisputeInvoiceCaseTypesSelector = createSelector([DisputeInvoiceCaseTypesSelector], function (caseTypes) {
  return map(function (caseType) {
    return {
      text: caseType.DisplayName,
      value: caseType.Id
    };
  }, sortWith([ascend(prop('DisplayName'))], caseTypes));
});
export var SelectedCaseInventoryIdSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer ? selectedCustomer.cases.selectedInventoryItem : null;
});
export var IsCaseEditModeSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer ? selectedCustomer.cases.data.isEditingCase : false;
});
export var CustomFieldsFromCurrentCategorySelector = createSelector([CurrentCaseSelector, SelectedCaseCategoryIdSelector, ActiveCaseTypesSelector, MetadataSelectors.codes.MetadataCodeTypeIntegerSelector(MetadataConstants.codes.RegularExpression), IsCaseEditModeSelector], function (currentCase, selectedCaseCategoryId, caseTypes, regexCodes, isEditMode) {
  if (selectedCaseCategoryId) {
    return map(function (additionalProperty) {
      var collection = [];
      if (additionalProperty.AdditionalPropertyValueType === AdditionalPropValueType.BOOLEAN) {
        collection = ['True', 'False'];
      } else if (additionalProperty.AdditionalPropertyValueType === AdditionalPropValueType.SELECT) {
        collection = pathOr([], ['PossibleValues'], additionalProperty);
      }
      return {
        id: additionalProperty.Id,
        type: additionalProperty.AdditionalPropertyValueType,
        name: additionalProperty.Name,
        value: isEditMode ? pathOr(null, ['Value'], find(propEq(additionalProperty.Id, 'Id'), pathOr([], ['AdditionalPropertyValues'], currentCase))) : null,
        required: additionalProperty.IsRequired,
        regex: additionalProperty.RegExCode ? new RegExp(String(MetadataHelpers.codes.getRegexForCode(regexCodes, additionalProperty.RegExCode))) : '',
        regexErrorMessage: pathOr('', ['Description'], RamdaHelper.findByValue(additionalProperty.RegExCode, regexCodes)),
        options: map(function (value) {
          return {
            id: value,
            name: value
          };
        }, collection),
        description: undefined
      };
    }, filter(function (property) {
      return property.Status === GENERAL_STATUSES.ACTIVE;
    }, sortWith([ascend(prop('DisplayOrder'))], pathOr([], ['CaseAdditionalProperties'], find(propEq(selectedCaseCategoryId, 'Id'), caseTypes)))));
  }
  return [];
});